// @flow

import React from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { media, buildRemarkText } from '../../helpers';
import {
  Title,
  Row,
  DiscountWrapper,
  FromPrice,
  Price,
  MileageAndDuration,
  MotorSpecs,
  FromPriceText,
  PackageName,
} from './atoms';
import {
  getBasePriceLabel,
  getAvailableLabel,
  getOccasionLinkTextSingle,
  getOccasionLinkTextPlural,
} from '../../store/componentSettings/selectors/pdbInfoSettings';
import { Flex } from '../grid';
import {
  getMake,
  getModel,
  getPrice,
  getEngine,
  getPower,
  getFueltype,
  hasDiscount,
  getDiscountPrice,
  getPackageName,
} from '../../store/pdb';
import Ribbon from '../ribbon';
import { getIsNotOrderable } from '../../store/pdb/selectors';
import type { Theme } from '../../types';

type OwnProps = {|
  showMotorSpecs: boolean,
  renderOccasions?: ?Function,
  hasRibbon: boolean,
  showOccasions?: boolean,
|};

type StateProps = {|
  basePriceLabel: string,
  make: string,
  model: string,
  engine: string,
  power: string,
  fueltype: string,
  price: string,
  discount: boolean,
  discountPrice: number,
  remarkText: string,
  showOccasions?: boolean,
  packageName?: ?string,
  isNotOrderable?: boolean,
  availableLabel: string, // eslint-disable-line react/no-unused-prop-types
  occasionLinkTextPlural: string, // eslint-disable-line react/no-unused-prop-types
  occasionLinkTextSingle: string, // eslint-disable-line react/no-unused-prop-types
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
|};

const PdbInfoContainer = styled<typeof Flex, Theme>(Flex)`
  flex-wrap: wrap;

  ${media.xl(css`
    flex-wrap: initial;
  `)}
`;

const PdbInfoComponent = styled<typeof Flex, Theme>(Flex)`
  max-width: 50%;
  /* stylelint-disable selector-type-no-unknown */
  &:not(:last-child) ${MileageAndDuration} {
    ${media.xl(css`
      padding-right: 1rem;

      span {
        display: block;
      }
    `)}
  }
`;

const RibbonSpacer = styled<any, Theme>('div')`
  height: 2rem;
`;

const PdbInfo = ({
  basePriceLabel,
  make,
  model,
  price,
  engine,
  power,
  fueltype,
  discount,
  discountPrice,
  showMotorSpecs,
  renderOccasions,
  remarkText,
  hasRibbon,
  packageName,
  showOccasions = false,
  isNotOrderable,
}: Props) => (
  <div>
    {hasRibbon && (
      <Ribbon
        title="JUSTLEASE"
        subTitle="TOP10"
        type="pdbInfo"
      />
    )}
    <Title>
      {`${make} ${model}`}
    </Title>
    {packageName && (
      <PackageName>{packageName}</PackageName>
    )}
    {showMotorSpecs && (
      <MotorSpecs>
        {`${engine} ${power} ${fueltype.toLowerCase()}`}
      </MotorSpecs>
    )}
    {hasRibbon && (
      <RibbonSpacer />
    )}
    {!isNotOrderable && (
      <Row>
        <PdbInfoContainer>
          <PdbInfoComponent alignItems="center">
            {discount && (
              <DiscountWrapper>
                <FromPrice displayCurrencySymbol amount={Number(price)} />
                <Price amount={discountPrice} decimalDash />
              </DiscountWrapper>
            )}
            {!discount && (
              <Price amount={Number(price)} decimalDash />
            )}
          </PdbInfoComponent>
          <PdbInfoComponent flexDirection="column" justifyContent="center">
            <FromPriceText>{basePriceLabel}</FromPriceText>
            {remarkText && (
              <MileageAndDuration>
                {remarkText}
              </MileageAndDuration>
            )}
          </PdbInfoComponent>
          {showOccasions && renderOccasions && renderOccasions()}
        </PdbInfoContainer>
      </Row>
    )}
  </div>
);

PdbInfo.defaultProps = {
  renderOccasions: null,
  showOccasions: false,
  packageName: null,
  isNotOrderable: false,
};

const mapStateToProps = (state): StateProps => ({
  basePriceLabel: getBasePriceLabel(state),
  availableLabel: getAvailableLabel(state),
  occasionLinkTextSingle: getOccasionLinkTextSingle(state),
  occasionLinkTextPlural: getOccasionLinkTextPlural(state),
  make: getMake(state),
  model: getModel(state),
  engine: getEngine(state),
  power: getPower(state),
  fueltype: getFueltype(state),
  price: getPrice(state),
  discount: hasDiscount(state),
  discountPrice: getDiscountPrice(state),
  remarkText: buildRemarkText(state),
  packageName: getPackageName(state),
  isNotOrderable: getIsNotOrderable(state),
});

export default connect<any, OwnProps, StateProps, any, any, any>(mapStateToProps)(PdbInfo);
